
import React, { useState, Suspense } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BarChart3Icon, 
  Users2Icon, 
  BanknoteIcon,
  CreditCardIcon
} from 'lucide-react'; 
import { BorrowingsDashboard } from '@/components/borrowings/BorrowingsDashboard';
import { LenderManagement } from '@/components/borrowings/LenderManagement';
import { DebtManagement } from '@/components/borrowings/DebtManagement';
import { RepaymentActivity } from '@/components/borrowings/RepaymentActivity';
import { BenposManagement } from '@/components/borrowings/benpos/BenposManagement';
import { Skeleton } from "@/components/ui/skeleton";

// Simple tab content loading fallback
const TabContentFallback = () => (
  <div className="w-full py-4">
    <Skeleton className="h-8 w-1/2 mb-4" />
    <Skeleton className="h-24 w-full mb-4" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
    </div>
  </div>
);

interface BorrowingsTabsProps {
  showAddLoanDialog: boolean;
  setShowAddLoanDialog: (show: boolean) => void;
  isLoading?: boolean;
}

export const BorrowingsTabs: React.FC<BorrowingsTabsProps> = ({ 
  showAddLoanDialog,
  setShowAddLoanDialog,
  isLoading = false
}) => {
  const [activeTab, setActiveTab] = useState("dashboard");

  // Memoize the content to prevent re-renders
  const renderTabContent = (value: string, Component: React.FC<any>) => (
    <TabsContent value={value} className="py-4">
      <Suspense fallback={<TabContentFallback />}>
        <Component />
      </Suspense>
    </TabsContent>
  );

  if (isLoading) {
    return (
      <div className="animate-pulse p-4">
        <div className="h-12 bg-muted rounded mb-6"></div>
        <div className="h-64 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid grid-cols-5 h-12 w-full">
        <TabsTrigger value="dashboard" className="flex items-center justify-center gap-2">
          <BarChart3Icon className="h-4 w-4" />
          <span className="hidden sm:inline">Dashboard</span>
        </TabsTrigger>
        <TabsTrigger value="lenders" className="flex items-center justify-center gap-2">
          <Users2Icon className="h-4 w-4" />
          <span className="hidden sm:inline">Lenders</span>
        </TabsTrigger>
        <TabsTrigger value="debt" className="flex items-center justify-center gap-2">
          <BanknoteIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Debt</span>
        </TabsTrigger>
        <TabsTrigger value="repayments" className="flex items-center justify-center gap-2">
          <CreditCardIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Lender Repayments</span>
        </TabsTrigger>
        <TabsTrigger value="benpos" className="flex items-center justify-center gap-2">
          <BanknoteIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Capital Markets Repayments</span>
        </TabsTrigger>
      </TabsList>

      {/* Only render the active tab to improve performance */}
      {activeTab === "dashboard" && renderTabContent("dashboard", BorrowingsDashboard)}
      {activeTab === "lenders" && renderTabContent("lenders", LenderManagement)}
      {activeTab === "debt" && renderTabContent("debt", DebtManagement)}
      {activeTab === "repayments" && renderTabContent("repayments", RepaymentActivity)}
      {activeTab === "benpos" && renderTabContent("benpos", BenposManagement)}
    </Tabs>
  );
};
